import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

interface BetaCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (code: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export const BetaCodeModal: React.FC<BetaCodeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  error = null
}) => {
  const [betaCode, setBetaCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!betaCode.trim()) {
      setLocalError('Please enter a beta access code');
      return;
    }

    try {
      await onSubmit(betaCode.trim().toUpperCase());
    } catch (_err) {
      // Error handling is done by parent component
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        // Don't allow closing during OAuth flow
        if (!isLoading) {
          onClose();
        }
      }}
      className="relative z-[1200]"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 dark:bg-black/60" aria-hidden="true" />

      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full bg-white dark:bg-gray-900 rounded-xl shadow-xl dark:shadow-2xl border border-gray-200 dark:border-gray-800">
          <div className="relative p-6">
            {/* Close button - only show if not in OAuth flow */}
            {!isLoading && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="bg-violet-100 dark:bg-violet-900/30 rounded-full p-3">
                <LockClosedIcon className="h-8 w-8 text-violet-600 dark:text-violet-400" />
              </div>
            </div>

            {/* Title */}
            <Dialog.Title className="text-2xl font-bold text-center text-gray-900 dark:text-gray-100 mb-2">
              Welcome to Vitly Beta
            </Dialog.Title>

            {/* Description */}
            <Dialog.Description className="text-center text-gray-600 dark:text-gray-400 mb-6">
              Vitly is currently in private beta. Please enter your invitation code to continue.
            </Dialog.Description>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Beta Access Code"
                name="betaCode"
                type="text"
                value={betaCode}
                onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                placeholder="Enter your access code"
                required
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                disabled={isLoading}
                className="text-center font-mono tracking-wider"
              />

              {/* Error messages */}
              {(error || localError) && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-md text-sm">
                  <p className="font-medium">Invalid Access Code</p>
                  <p className="mt-1">{error || localError}</p>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                fullWidth
                isLoading={isLoading}
                disabled={isLoading || !betaCode.trim()}
              >
                {isLoading ? 'Verifying...' : 'Continue'}
              </Button>

              {/* Additional help text */}
              <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
                Don&apos;t have a code?{' '}
                <a
                  href="https://vitly.com/beta-request"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium"
                >
                  Request access
                </a>
              </p>
            </form>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default BetaCodeModal;
